import React, { useState } from 'react';
import { Product, CryptoAsset } from '../types';
import { X, Trash2, Wallet, Lock, Loader2, ArrowRight, Coins, ExternalLink, WifiOff, AlertTriangle, ShieldCheck, CornerDownLeft } from 'lucide-react';
import { processPayment } from '../services/payment';
import { useLanguage } from '../services/languageContext';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: Product[];
  onRemoveItem: (id: string) => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose, cartItems, onRemoveItem }) => {
  const { t } = useLanguage();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<CryptoAsset>('USDT');
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  
  // Calculations
  const subtotal = cartItems.reduce((sum, item) => sum + (item.originalPrice || item.price), 0);
  const total = cartItems.reduce((sum, item) => sum + item.price, 0);
  const savings = subtotal - total;

  const assets: { id: CryptoAsset; name: string; icon: string }[] = [
    { id: 'USDT', name: 'Tether', icon: '₮' },
    { id: 'TON', name: 'Toncoin', icon: '💎' },
    { id: 'BTC', name: 'Bitcoin', icon: '₿' },
    { id: 'ETH', name: 'Ethereum', icon: 'Ξ' },
    { id: 'LTC', name: 'Litecoin', icon: 'Ł' },
  ];

  const handleInitialClick = () => {
    setErrorDetails(null);
    setShowInstructionModal(true);
  };

  const handleProceedToPayment = async () => {
    setIsProcessing(true);
    setStatusMsg(t('pay_status_connect'));
    setErrorDetails(null);
    
    try {
      // Give UI a moment to update
      await new Promise(r => setTimeout(r, 200));
      
      const result = await processPayment(cartItems, selectedAsset);
      
      if (result.success && result.invoiceUrl && result.invoiceId) {
        setStatusMsg(t('pay_status_redirect'));
        
        // CRITICAL: Save ID to verify status when user returns
        localStorage.setItem('ofm_pending_invoice', result.invoiceId.toString());
        
        setTimeout(() => {
             window.open(result.invoiceUrl, '_blank');
             setShowInstructionModal(false);
             onClose();
             setIsProcessing(false);
             setStatusMsg("");
        }, 1000);
      } else {
        setIsProcessing(false);
        setErrorDetails(result.message || "Gateway Timeout");
      }
    } catch (error) {
      setIsProcessing(false);
      setErrorDetails("Connection Error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#000]/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Panel */}
      <div className="relative w-full max-w-md h-full max-h-[100dvh] bg-[#050505] border-l border-white/10 shadow-2xl flex flex-col animate-slide-up overscroll-contain">
        
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-[#0A0A0A]">
          <div>
            <h2 className="text-xl font-display font-bold text-white tracking-wide">{t('cart_title')}</h2>
            <span className="text-[10px] text-gray-500 uppercase tracking-widest">SESSION: {Math.random().toString(36).substr(2, 6).toUpperCase()}</span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Secure Strip */}
        <div className="bg-gradient-to-r from-green-900/20 to-transparent border-b border-green-500/10 p-2 flex items-center gap-3 px-6">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest">{t('cart_encrypted')}</span>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-6 opacity-30">
              <div className="w-20 h-20 border border-dashed border-white rounded-full flex items-center justify-center">
                 <Wallet size={32} />
              </div>
              <p className="font-mono text-sm uppercase">{t('cart_empty_text')}</p>
            </div>
          ) : (
            cartItems.map((item, index) => (
              <div key={`${item.id}-${index}`} className="group flex gap-4 p-4 bg-[#0A0A0A] rounded-xl border border-white/5 hover:border-white/20 transition-all">
                <div className="w-16 h-16 bg-gray-900 rounded-lg overflow-hidden shrink-0 border border-white/5 relative">
                  <img src={item.coverImage} alt={item.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                  {item.originalPrice && item.price < item.originalPrice && (
                      <div className="absolute top-0 right-0 bg-red-500 text-[8px] font-bold text-black px-1">{t('sale_tag')}</div>
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-white font-bold text-sm leading-tight line-clamp-1">{item.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                       <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 uppercase">{item.categories[0]}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-end">
                    <div className="flex flex-col">
                        {item.originalPrice && (
                            <span className="text-[10px] text-gray-500 line-through">${item.originalPrice}</span>
                        )}
                        <span className="text-white font-mono text-sm font-bold">${item.price}</span>
                    </div>
                    <button 
                      onClick={() => onRemoveItem(item.id)}
                      className="text-gray-600 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-6 bg-[#0A0A0A] border-t border-white/10 pb-10">
            
            {/* Asset Selector */}
            <div className="mb-6">
                <label className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-3 block">{t('cart_select_asset')}</label>
                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
                    {assets.map((asset) => (
                        <button
                            key={asset.id}
                            onClick={() => setSelectedAsset(asset.id)}
                            className={`
                                flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold transition-all whitespace-nowrap
                                ${selectedAsset === asset.id 
                                    ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.2)]' 
                                    : 'bg-[#0F0F12] border-white/10 text-gray-400 hover:border-white/30 hover:text-white'}
                            `}
                        >
                            <span>{asset.icon}</span>
                            <span>{asset.id}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-3 mb-8 bg-white/[0.02] p-4 rounded-xl border border-white/5">
              <div className="flex justify-between text-gray-500 text-xs font-medium uppercase tracking-wider">
                <span>{t('cart_subtotal')}</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {savings > 0 && (
                  <div className="flex justify-between text-green-500 text-xs font-bold uppercase tracking-wider">
                    <span>{t('cart_discounts')}</span>
                    <span>-${savings.toFixed(2)}</span>
                  </div>
              )}
              <div className="flex justify-between items-end pt-4 border-t border-white/10">
                <span className="text-sm font-bold text-white">{t('cart_total_due')}</span>
                <span className="font-display font-bold text-3xl text-white">
                    ${total.toFixed(2)}
                </span>
              </div>
            </div>

            <button 
              onClick={handleInitialClick}
              className="w-full bg-white text-black font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all hover:bg-gray-200 active:scale-[0.98] shadow-glass"
            >
                <span className="text-sm uppercase tracking-wider">{t('cart_proceed')}</span>
                <ArrowRight size={18} />
            </button>
            
            <div className="flex justify-center items-center gap-2 mt-6 opacity-30">
              <Lock size={10} />
              <span className="text-[9px] uppercase tracking-[0.2em]">{t('cart_secured_by')}</span>
            </div>
          </div>
        )}
      </div>

      {/* --- PREMIUM INSTRUCTION POPUP (REDESIGNED) --- */}
      {showInstructionModal && (
          <div className="absolute inset-0 z-[110] bg-[#020203]/95 backdrop-blur-xl flex items-center justify-center p-6 animate-fade-in">
              <div className="w-full max-w-md bg-[#050505] border border-white/10 rounded-3xl p-8 relative shadow-2xl overflow-hidden group">
                  
                  {/* Background Effects */}
                  <div className="absolute inset-0 bg-noise opacity-[0.05] pointer-events-none" />
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-indigo-500/10 blur-[50px] rounded-full pointer-events-none" />
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent animate-shine bg-[length:200%_auto]" />

                  {/* Header */}
                  <div className="flex justify-between items-start mb-8 relative z-10">
                      <div>
                        <h3 className="text-xl font-display font-bold text-white uppercase tracking-tight mb-1">
                            {errorDetails ? 'System Protocol Error' : t('pay_protocol')}
                        </h3>
                        <div className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${errorDetails ? 'bg-red-500' : 'bg-green-500'} animate-pulse`} />
                            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">
                                {errorDetails ? 'CONNECTION SEVERED' : 'SECURE CHANNEL ACTIVE'}
                            </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowInstructionModal(false)} 
                        className="p-2 text-gray-500 hover:text-white bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                      >
                        <X size={18} />
                      </button>
                  </div>

                  {errorDetails ? (
                     // ERROR STATE
                     <div className="animate-fade-in relative z-10">
                        <div className="p-4 bg-red-900/10 border border-red-900/30 rounded-xl mb-6 flex gap-4 items-start">
                           <AlertTriangle className="text-red-500 shrink-0 mt-1" size={20} />
                           <div>
                               <p className="text-sm text-red-200 font-bold mb-1">Connection Blocked</p>
                               <p className="text-xs text-red-400/80 leading-relaxed">{errorDetails}</p>
                           </div>
                        </div>
                        <button 
                          onClick={handleProceedToPayment}
                          disabled={isProcessing}
                          className="w-full bg-white text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 uppercase text-xs tracking-widest hover:bg-gray-200"
                        >
                           {isProcessing ? <Loader2 className="animate-spin" size={16}/> : <span>Retry Handshake</span>}
                        </button>
                     </div>
                  ) : (
                    // NORMAL PROTOCOL STEPS
                    <div className="animate-fade-in relative z-10">
                      <p className="text-sm text-gray-400 mb-8 leading-relaxed border-l-2 border-white/10 pl-4">
                          {t('pay_redirect')}
                      </p>

                      <div className="space-y-6 mb-10">
                          {/* Step 1 */}
                          <div className="flex gap-4 group/step">
                              <div className="w-8 h-8 rounded-full bg-[#1A1A1A] text-gray-400 group-hover/step:text-indigo-400 group-hover/step:bg-indigo-500/10 border border-white/5 flex items-center justify-center text-xs font-bold shrink-0 transition-all">01</div>
                              <div>
                                  <p className="text-xs font-bold text-white uppercase mb-1 tracking-wider">{t('pay_step_1')}</p>
                                  <p className="text-[11px] text-gray-500 leading-relaxed">{t('pay_step_1_desc', { asset: selectedAsset })}</p>
                              </div>
                          </div>
                          
                          {/* Step 2 */}
                          <div className="flex gap-4 group/step">
                              <div className="w-8 h-8 rounded-full bg-[#1A1A1A] text-gray-400 group-hover/step:text-indigo-400 group-hover/step:bg-indigo-500/10 border border-white/5 flex items-center justify-center text-xs font-bold shrink-0 transition-all">02</div>
                              <div>
                                  <p className="text-xs font-bold text-white uppercase mb-1 tracking-wider">{t('pay_step_2')}</p>
                                  <p className="text-[11px] text-gray-500 leading-relaxed">{t('pay_step_2_desc')}</p>
                              </div>
                          </div>

                          {/* Step 3 (Highlight) */}
                          <div className="flex gap-4 group/step relative">
                              <div className="absolute inset-0 bg-indigo-500/5 -m-2 rounded-xl border border-indigo-500/20" />
                              <div className="w-8 h-8 rounded-full bg-indigo-500 text-black border border-indigo-400 flex items-center justify-center text-xs font-bold shrink-0 shadow-[0_0_15px_rgba(99,102,241,0.5)] z-10">03</div>
                              <div className="z-10">
                                  <p className="text-xs font-bold text-indigo-300 uppercase mb-1 tracking-wider flex items-center gap-2">
                                    {t('pay_step_3')} <CornerDownLeft size={12} />
                                  </p>
                                  <p className="text-[11px] text-indigo-200/70 leading-relaxed">{t('pay_step_3_desc')}</p>
                              </div>
                          </div>
                      </div>

                      <button 
                          onClick={handleProceedToPayment}
                          disabled={isProcessing}
                          className="w-full bg-white hover:bg-gray-200 text-black font-display font-black py-4 rounded-xl flex items-center justify-center gap-2 uppercase text-xs tracking-widest transition-all shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed group/btn"
                      >
                          {isProcessing ? (
                              <>
                                <Loader2 className="animate-spin" size={16} />
                                <span>{statusMsg}</span>
                              </>
                          ) : (
                              <>
                                <span>{t('pay_btn_open')}</span>
                                <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform"/>
                              </>
                          )}
                      </button>
                    </div>
                  )}
              </div>
          </div>
      )}
    </div>
  );
};
